import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { BookOpen, Loader2, Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AccountTree } from "@/components/puc/AccountTree";
import { AdoptMissingClassesDialog } from "@/components/puc/AdoptMissingClassesDialog";
import { AdoptTemplateDialog } from "@/components/puc/AdoptTemplateDialog";
import { CreateCustomAccountDialog } from "@/components/puc/CreateCustomAccountDialog";
import { RenameAccountDialog } from "@/components/puc/RenameAccountDialog";
import {
  useCompanyPuc,
  useDeleteCustomAccount,
  useRenameAccount,
} from "@/lib/api/puc.queries";
import { ApiError } from "@/lib/api";
import { useActiveCompany } from "@/lib/company-context";
import { cn } from "@/lib/cn";
import type { PucEffectiveAccount, PucLevel } from "@/lib/api/puc";

export const Route = createFileRoute("/companies/$companyId/puc/")({
  component: PucIndexPage,
});

type ClassFilter = "all" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9";

function PucIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PucIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function PucIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  // Sync the URL companyId into the active context (mirrors terceros).
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [q, setQ] = useState("");
  const [classFilter, setClassFilter] = useState<ClassFilter>("all");
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<PucEffectiveAccount | null>(
    null,
  );
  const [createParent, setCreateParent] = useState<PucEffectiveAccount | null>(
    null,
  );

  // Pull the full effective list (template overlay + custom). We filter
  // client-side because the BFF returns at most ~600 rows and the search
  // boxes need to match both adopted and not-yet-adopted rows.
  const pucQuery = useCompanyPuc(companyId, { onlyActive: false });
  const renameMutation = useRenameAccount(companyId);
  const deleteMutation = useDeleteCustomAccount(companyId);

  const accounts = useMemo(
    () => pucQuery.data?.accounts ?? [],
    [pucQuery.data?.accounts],
  );
  const adopted = useMemo(
    () => accounts.filter((a) => a.is_active || a.source !== "template"),
    [accounts],
  );

  const filteredAccounts = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return adopted.filter((a) => {
      if (classFilter !== "all" && a.code[0] !== classFilter) return false;
      if (!needle) return true;
      return (
        a.code.toLowerCase().includes(needle) ||
        a.name.toLowerCase().includes(needle)
      );
    });
  }, [adopted, classFilter, q]);

  // Set of class codes present in the company's adopted PUC, used to scope
  // the filter chips so the user only sees classes they actually have.
  const visibleClasses = useMemo(() => {
    const out = new Set<string>();
    for (const a of adopted) out.add(a.code[0]!);
    return Array.from(out).sort();
  }, [adopted]);

  const onRename = (a: PucEffectiveAccount) => {
    setRenameTarget(a);
    setRenameOpen(true);
  };

  const onToggleActive = async (a: PucEffectiveAccount) => {
    try {
      await renameMutation.mutateAsync({
        code: a.code,
        input: { isActive: !a.is_active },
      });
      toast.success(
        a.is_active ? t("puc.success.deactivated") : t("puc.success.activated"),
      );
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      toast.error(
        code ? t([`puc.error.${code}`, "common.error"]) : t("common.error"),
      );
    }
  };

  const onAddChild = (parent: PucEffectiveAccount) => {
    setCreateParent(parent);
    setCreateOpen(true);
  };

  const onDelete = async (a: PucEffectiveAccount) => {
    try {
      await deleteMutation.mutateAsync(a.code);
      toast.success(t("puc.success.deleted"));
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      toast.error(
        code ? t([`puc.error.${code}`, "common.error"]) : t("common.error"),
      );
    }
  };

  const isEmpty = !pucQuery.isPending && adopted.length === 0;
  const adoption = pucQuery.data?.adoption;
  const isPartial = adoption?.partial === true;
  const missingClassCodes = adoption?.missingClassCodes ?? [];
  const missingClassLabels = missingClassCodes
    .map((code) => t(`puc.class.${code}`))
    .join(", ");

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {t("puc.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("puc.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("puc.subtitle")}
          </p>
        </div>
        {!isEmpty && (
          <div className="flex flex-wrap gap-2">
            {isPartial && (
              <Button variant="outline" onClick={() => setMissingOpen(true)}>
                {t("puc.adopt.missingClasses")}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                setCreateParent(null);
                setCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              {t("puc.action.addCustom")}
            </Button>
          </div>
        )}
      </div>

      {pucQuery.isError ? (
        <ErrorState
          title={t("puc.error.loadTitle")}
          description={t("puc.error.loadBody")}
          onRetry={() => void pucQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : pucQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : isEmpty ? (
        <EmptyState onAdopt={() => setAdoptOpen(true)} />
      ) : (
        <>
          {isPartial && (
            <Alert variant="warning">
              <AlertTitle>{t("puc.partial.title")}</AlertTitle>
              <AlertDescription>
                {t("puc.partial.body", { classes: missingClassLabels })}
              </AlertDescription>
            </Alert>
          )}
          <div className="sticky top-0 z-10 -mx-2 flex flex-col gap-3 bg-background/80 px-2 pb-2 pt-1 backdrop-blur sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("puc.search")}
                className="pl-8"
              />
            </div>
          </div>

          <ClassFilterChips
            value={classFilter}
            onChange={setClassFilter}
            visibleClasses={visibleClasses}
          />

          {filteredAccounts.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
                <h2 className="text-lg font-semibold">
                  {t("puc.empty.search")}
                </h2>
              </CardContent>
            </Card>
          ) : (
            <AccountTree
              accounts={filteredAccounts}
              forceExpanded={q.trim().length > 0}
              onRename={onRename}
              onToggleActive={onToggleActive}
              onAddChild={onAddChild}
              onDelete={onDelete}
            />
          )}
        </>
      )}

      <AdoptTemplateDialog
        companyId={companyId}
        open={adoptOpen}
        onOpenChange={setAdoptOpen}
      />
      <AdoptMissingClassesDialog
        companyId={companyId}
        open={missingOpen}
        onOpenChange={setMissingOpen}
        missingClassCodes={missingClassCodes}
      />
      <CreateCustomAccountDialog
        companyId={companyId}
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) setCreateParent(null);
        }}
        parent={createParent}
        candidateParents={adopted}
      />
      <RenameAccountDialog
        companyId={companyId}
        account={renameTarget}
        open={renameOpen}
        onOpenChange={(open) => {
          setRenameOpen(open);
          if (!open) setRenameTarget(null);
        }}
      />
    </div>
  );
}

function EmptyState({ onAdopt }: { onAdopt: () => void }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <BookOpen className="h-8 w-8 text-primary" />
        </div>
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">{t("puc.empty.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("puc.empty.body")}</p>
        </div>
        <Button onClick={onAdopt}>{t("puc.empty.action")}</Button>
      </CardContent>
    </Card>
  );
}

function ClassFilterChips({
  value,
  onChange,
  visibleClasses,
}: {
  value: ClassFilter;
  onChange: (next: ClassFilter) => void;
  visibleClasses: string[];
}) {
  const { t } = useTranslation();
  const chips: { value: ClassFilter; labelKey: string }[] = [
    { value: "all", labelKey: "puc.filter.all" },
    ...visibleClasses.map((c) => ({
      value: c as ClassFilter,
      labelKey: `puc.class.${c}`,
    })),
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => {
        const active = chip.value === value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => onChange(chip.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              active
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {t(chip.labelKey)}
          </button>
        );
      })}
    </div>
  );
}

// Re-exported to satisfy `import type { PucLevel }` lint usage above.
export type { PucLevel };
